/**
 * Bubbles Visualizer - Floating circles representing frequencies
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';

// Metadata for manifest generation
export const metadata = {
  name: 'bubbles',
  displayName: 'Floating Bubbles',
  contextType: '2d',
};

class BubblesVisualizer extends Base2DVisualizer {
  static name = 'bubbles';
  static displayName = 'Floating Bubbles';

  /**
   * Get configuration UI definition
   */
  static getConfigUI() {
    return [
      { type: 'range', id: 'bubbleCount', label: 'Bubble Count', min: 8, max: 128, default: 32 },
      { type: 'range', id: 'minRadius', label: 'Min Radius', min: 5, max: 50, default: 10, unit: 'px' },
      { type: 'range', id: 'maxRadius', label: 'Max Radius', min: 30, max: 200, default: 80, unit: 'px' },
      { type: 'range', id: 'smoothing', label: 'Smoothing', min: 0, max: 95, default: 85, scale: 0.01 },
      { type: 'range', id: 'floatSpeed', label: 'Float Speed', min: 1, max: 20, default: 5, scale: 0.1 },
      { type: 'range', id: 'opacity', label: 'Opacity', min: 20, max: 100, default: 70, scale: 0.01 },
      { type: 'color', id: 'colorStart', label: 'Low Freq Color', default: '#00ff88' },
      { type: 'color', id: 'colorEnd', label: 'High Freq Color', default: '#ff0088' },
      { type: 'color', id: 'backgroundColor', label: 'Background', default: '#0a0a0a' },
      { type: 'checkbox', id: 'glow', label: 'Glow effect', default: true },
      { type: 'checkbox', id: 'useLogBins', label: 'Log Frequency Bins', default: false }
    ];
  }

  /**
   * Visualizer-specific default configuration
   */
  getDefaultConfig() {
    return {
      bubbleCount: 32,
      minRadius: 10,
      maxRadius: 80,
      opacity: 0.7,
      floatSpeed: 0.5,
      useLogBins: false,
    };
  }

  /**
   * Initialize bubbles
   */
  init() {
    this.bubbles = [];
    this.time = 0;
    this.previousSemitones = new Float32Array(this.config.bubbleCount);
    this.initBubbles();
  }

  /**
   * Initialize bubble positions and properties
   */
  initBubbles() {
    const { bubbleCount } = this.config;
    this.bubbles = [];

    for (let i = 0; i < bubbleCount; i++) {
      this.bubbles.push({
        // Random initial position
        x: Math.random() * this.canvas.width,
        y: Math.random() * this.canvas.height,
        // Velocity for floating motion
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        // Phase offset for organic motion
        phaseX: Math.random() * Math.PI * 2,
        phaseY: Math.random() * Math.PI * 2,
        // Frequency band index (0-1)
        freqIndex: i / bubbleCount
      });
    }
  }

  /**
   * Returns the number of bubbles
   */
  getElementCount() {
    return this.config.bubbleCount;
  }

  /**
   * Handle configuration changes
   */
  onConfigChange(changed) {
    if (changed.bubbleCount !== undefined) {
      this.initBubbles();
      this.previousAmplitudes = new Array(this.config.bubbleCount).fill(0);
      this.previousSemitones = new Float32Array(this.config.bubbleCount);
    }
    if (changed.useLogBins !== undefined) {
      this.previousSemitones = new Float32Array(this.config.bubbleCount);
    }
  }

  /**
   * Map 96 semitone bins to bubbleCount using averaging, with smoothing
   * Uses cached buffers to avoid per-frame allocations
   */
  mapSemitonesToBubbles(semitones, bubbleCount, smoothing) {
    // Allocate/reallocate buffers only when size changes
    if (!this.previousSemitones || this.previousSemitones.length !== bubbleCount) {
      this.previousSemitones = new Float32Array(bubbleCount);
      this._semitoneResultBuffer = new Float32Array(bubbleCount);
    }

    const result = this._semitoneResultBuffer;
    const binsPerBubble = semitones.length / bubbleCount;

    for (let i = 0; i < bubbleCount; i++) {
      const start = Math.floor(i * binsPerBubble);
      const end = Math.floor((i + 1) * binsPerBubble);
      let sum = 0;
      for (let j = start; j < end; j++) {
        sum += semitones[j];
      }
      const rawValue = sum / (end - start);
      result[i] = this.previousSemitones[i] * smoothing + rawValue * (1 - smoothing);
      this.previousSemitones[i] = result[i];
    }
    return result;
  }

  /**
   * Render a single frame
   */
  renderFrame(amplitudes, features) {
    const { ctx, canvas, config, bubbles } = this;
    const { minRadius, maxRadius, opacity, floatSpeed, useLogBins } = config;

    // Use semitones (log bins) if enabled and features available
    let data = amplitudes;
    if (useLogBins && features?.pitch) {
      const semitones = features.pitch.semitones();
      data = this.mapSemitonesToBubbles(semitones, bubbles.length, config.smoothing);
    }

    // Update time for floating motion
    this.time += 0.016 * floatSpeed; // ~60fps timestep scaled by speed

    // Update and draw each bubble
    for (let i = 0; i < bubbles.length; i++) {
      const bubble = bubbles[i];
      const amplitude = data[i];

      // Calculate radius based on amplitude
      const radius = minRadius + amplitude * (maxRadius - minRadius);

      // Update position with floating motion
      const floatX = Math.sin(this.time + bubble.phaseX) * 0.5;
      const floatY = Math.cos(this.time * 0.7 + bubble.phaseY) * 0.5;

      bubble.x += bubble.vx * floatSpeed * 0.1 + floatX;
      bubble.y += bubble.vy * floatSpeed * 0.1 + floatY;

      // Add slight attraction to center to prevent clustering at edges
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const dx = centerX - bubble.x;
      const dy = centerY - bubble.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 0) {
        bubble.x += (dx / dist) * 0.1;
        bubble.y += (dy / dist) * 0.1;
      }

      // Bounce off edges (with padding for radius)
      const padding = maxRadius;
      if (bubble.x < padding) {
        bubble.x = padding;
        bubble.vx *= -0.8;
      }
      if (bubble.x > canvas.width - padding) {
        bubble.x = canvas.width - padding;
        bubble.vx *= -0.8;
      }
      if (bubble.y < padding) {
        bubble.y = padding;
        bubble.vy *= -0.8;
      }
      if (bubble.y > canvas.height - padding) {
        bubble.y = canvas.height - padding;
        bubble.vy *= -0.8;
      }

      // Get color based on frequency (low freq = colorStart, high freq = colorEnd)
      const color = this.lerpColor(bubble.freqIndex);

      // Draw bubble
      ctx.beginPath();
      ctx.arc(bubble.x, bubble.y, radius, 0, Math.PI * 2);

      // Glow effect
      if (config.glow) {
        ctx.shadowBlur = 20 + amplitude * 30;
        ctx.shadowColor = color;
      }

      // Fill with semi-transparent color
      ctx.fillStyle = color;
      ctx.globalAlpha = opacity * (0.5 + amplitude * 0.5);
      ctx.fill();

      // Add highlight for 3D soap bubble effect
      ctx.globalAlpha = 0.3 * (0.5 + amplitude * 0.5);
      const highlightGradient = ctx.createRadialGradient(
        bubble.x - radius * 0.3,
        bubble.y - radius * 0.3,
        0,
        bubble.x,
        bubble.y,
        radius
      );
      highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
      highlightGradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.2)');
      highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = highlightGradient;
      ctx.fill();

      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
    }
  }

  /**
   * Clear the canvas and reset state
   */
  clear() {
    super.clear();
    this.time = 0;
  }
}

// Register with the visualizer registry
VisualizerRegistry.register(BubblesVisualizer);

export { BubblesVisualizer };
export default BubblesVisualizer;
